import { useEffect, useState } from 'react';

import { createAdminApi, type AdminAuditEvent, type AdminPage } from '@/admin/adminApi';
import { httpStatusOf, messageOf } from '@/admin/users/adminErrors';
import { useHttpClient } from '@/app/providers';

import { readFilters, toQuery, writeFilters, type AuditFilters } from './auditFilters';

/**
 * Chargement d'une page du journal.
 *
 * Meme patron que la liste des comptes : l'issue est rangee avec la **clef** de
 * la demande qui l'a produite, si bien que l'attente se deduit au lieu de
 * s'ecrire (un `setState` pose dans l'effet ajouterait un rendu a chaque
 * passage). La derniere page obtenue reste affichee apres un echec de
 * rafraichissement : une erreur ne doit pas effacer ce que l'exploitant lisait.
 *
 * **Une lecture du journal s'inscrit elle-meme au journal** (`admin.access`,
 * ADR 0032). Rien ici ne relance donc une requete tout seul : ni minuterie, ni
 * frappe — seulement un changement de filtre, de page ou de tentative
 * (`attempt`, que « Actualiser » et le retour sur l'onglet incrementent).
 */

export interface AuditLoadError {
  readonly message: string;
  /** Statut HTTP, ou `null` hors reponse du serveur (reseau, session). */
  readonly status: number | null;
}

export interface AuditPageState {
  readonly busy: boolean;
  readonly page: AdminPage<AdminAuditEvent> | null;
  readonly error: AuditLoadError | null;
}

interface Outcome {
  readonly key: string;
  readonly page: AdminPage<AdminAuditEvent> | null;
  readonly error: AuditLoadError | null;
}

export const useAuditEvents = ({
  enabled,
  filters,
  page,
  perPage,
  attempt,
}: {
  readonly enabled: boolean;
  readonly filters: AuditFilters;
  readonly page: number;
  readonly perPage: number;
  readonly attempt: number;
}): AuditPageState => {
  const http = useHttpClient();
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // Les filtres sont releves par leur forme d'URL : un objet recree a chaque
  // rendu relancerait l'effet, une chaine identique non.
  const filtersKey = writeFilters(filters).toString();
  const key = `${filtersKey}|${String(page)}|${String(perPage)}|${String(attempt)}`;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        // « Maintenant » est lu **ici**, au chargement : une periode relative
        // (« 7 derniers jours ») glisse avec chaque actualisation.
        const query = toQuery(readFilters(new URLSearchParams(filtersKey)), new Date());
        const result = await createAdminApi(http).listAuditEvents(query, page, perPage);

        if (!cancelled) {
          setOutcome({ key, page: result, error: null });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setOutcome((previous) => ({
            key,
            page: previous?.page ?? null,
            error: { message: messageOf(error), status: httpStatusOf(error) },
          }));
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [http, enabled, filtersKey, page, perPage, key]);

  const current = outcome?.key === key ? outcome : null;

  return {
    busy: enabled && current === null,
    page: outcome?.page ?? null,
    // Une erreur ne survit pas a la demande qui l'a produite.
    error: current?.error ?? null,
  };
};
